import SmartImg from "./smartImg";
import React, { useContext, useEffect, useId, useMemo, useState } from "react";
import {graphql, Link, useStaticQuery} from "gatsby";
import LanguageContext from "../context/LanguageContext";
import { getSubmissionErrorMessage, submitFormData } from "../apiService";
import logo from "../images/favicon.svg";

const Footer = () => {
    const {language} = useContext(LanguageContext);

    // The footer renders on every page alongside other forms, so its field ids
    // must be scoped — a duplicate id makes a <label> click focus the wrong form.
    const uid = useId();
    const fieldId = (name) => `${name}-${uid}`;

    const data = useStaticQuery(graphql`
        {
          strapi {           
            navigations (
                filters: { Name: { eq: "Bottom navigation" },
                locale: { in: ["de", "en"] }
            } ) {
                documentId
                Name
                locale
                Items {
                  Name
                  Link {
                    Name
                    Page {
                      Slug
                    }
                  }
                  Children {
                    Name
                    Link {
                      Name
                      Page {
                        Slug
                      }
                    }
                  }
                }
            }
          }
        }
    `)

    const translations = useMemo(() =>  ({
        en: {
            formHeadline: "Subscribe our Newsletter",
            formParagraph: "Sign up for our newsletter to stay up-to-date and not miss any news, blog articles, etc.",
            formDataPrivacy: "Your data security is important to us. Please read our privacy policy.",
            formDataPrivacyAccepted: "I have read and understood the privacy policy.",
            formSubmit: "Subscribe",
            formMailPlaceholder: "Enter email address",
            notificationSuccessTitle: "Successfully submitted!",
            notificationSuccessMessage: "Thank you for subscribing to our newsletter.\n",
            notificationErrorTitle: "An error occurred",
            notificationErrorMessage: "Have you confirmed the privacy policy?",
            transmissionError: "There was a problem with your submission. Please try again.",
            consentText: "I agree to allow Leaderfy GmbH to store and process my personal data."
        },
        de: {
            formHeadline: "Abonnieren Sie unseren Newsletter",
            formParagraph: "Melden sich für unseren Newsletter an, um keine Neuigkeiten, Blogartikel etc. zu verpassen.",
            formDataPrivacy: "Ihre Datensicherheit ist uns ein Anliegen. Lesen Sie unsere Datenschutz-Richtlinien.",
            formDataPrivacyAccepted: "Ich habe die Datenschutz-Richtlinien gelesen und verstanden",
            formSubmit: "Abonnieren",
            formMailPlaceholder: "E-Mail-Adresse eintragen",
            notificationSuccessTitle: "Erfolgreich übermittelt!",
            notificationSuccessMessage: "Vielen Dank für das Abonnieren unseres Newsletters.",
            notificationErrorTitle: "Es ist ein Fehler aufgetreten.",
            notificationErrorMessage: "Haben Sie die Datenschutz-Richtlinie bestätigt?",
            transmissionError: "Es gibt einen Übertragungsfehler. Bitte versuchen Sie es erneut.",
            consentText: "Hiermit erlaube ich der Leaderfy GmbH meine persönlichen Daten zu speichern und zu verarbeiten."
        }
    }), []);

    const [translation, setTranslation] = useState(translations.en);

    useEffect(() => {
        setTranslation(translations[language] || translations.en);
    }, [language, translations]);

    const {
        formHeadline,
        formParagraph,
        formDataPrivacy,
        formDataPrivacyAccepted,
        formSubmit,
        notificationSuccessTitle,
        notificationSuccessMessage,
        notificationErrorTitle,
        notificationErrorMessage,
        transmissionError,
        consentText,
        formMailPlaceholder
    } = translation;


    let bottomNavigationData;
    data.strapi.navigations.some((navData) => {
        if(navData.locale === language){
            bottomNavigationData = navData
            return true
        }
        return false
    });

    const initialFormData = { hp_field: '', email: '', consent: false };
    const [formData, setFormData] = useState(initialFormData);
    const [showNotification, setShowNotification] = useState({ show: false, consent: false });

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Honeypot: only bots fill the hidden field. Show them the same
        // confirmation a human gets rather than a dead button, and send nothing.
        if (formData.hp_field?.trim()) {
            setShowNotification({ show: true, consent: true });
            return;
        }

        const accountId = '25621956';
        const formId = '10872b2d-a1b7-4a19-bab1-e6c67b647ef6';

        const backendEndpoint = `${process.env.GATSBY_NEWSLETTER_BACKEND_BASE}/submit/${accountId}/${formId}`;

        const submissionData = {
            submittedAt: Date.now(),
            fields: [
                {
                    objectTypeId: "0-1",
                    name: "email",
                    value: formData.email
                }
            ],
            context: {
                pageUri: "www.leaderfy.io",
                pageName: "Leaderfy Newsletter"
            },
            legalConsentOptions: {
                consent: {
                    consentToProcess: formData.consent,
                    text: consentText
                }
            }
        };

        try {
            if(formData.consent) {
                await submitFormData(submissionData, backendEndpoint);
                setShowNotification({show: true, consent: true});
            } else {
                setShowNotification({show: true, consent: false});
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            // A failed submission must not render as the success dialog.
            setShowNotification({
                show: true,
                consent: false,
                message: getSubmissionErrorMessage(error, language, transmissionError),
            });
        }
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: newValue,
        }));
    };

    const closeNotificationMessage = function() {
        setShowNotification(false);
    }

    return (
        <footer className="bg-gray-800">
            <h2 id="footer-heading" className="sr-only">Footer</h2>
            <div className="mx-auto max-w-7xl pb-4 pl-8 pr-8 pt-12">
                <div className="block">
                    <Link to={'/' + language} className="-m-1.5 p-1.5">
                        <SmartImg className="h-14 w-14 m-0" alt="Leaderfy Logo" src={logo} />
                    </Link>
                    <div className="mt-8 grid md:grid-cols-1 lg:grid-cols-2 gap-8">
                        {bottomNavigationData.Items.map((navItem, index) => (
                          <div key={navItem.Name} className="grid grid-cols-2 gap-8">
                              <div className="mt-0">
                                  <h3 className="text-sm leading-6 font-semibold text-white">{navItem.Name}</h3>
                                  <ul className="m-0 mt-6">
                                      {navItem.Children.map((navSubItem) => (
                                        <li key={navSubItem.Name} className="text-white">
                                            <Link to={
                                                navSubItem.Link?.Page?.Slug === '/'
                                                  ? `/${language}/`
                                                  : `/${language}/${navSubItem.Link?.Page?.Slug}`
                                            } className="text-sm leading-6 text-gray-300 no-underline">
                                                {navSubItem.Name}
                                            </Link>
                                        </li>
                                      ))}
                                  </ul>
                              </div>
                              {index % 2 === 0 && navItem.Children.length > 0 && (
                                <div className="mt-0">
                                </div>
                              )}
                          </div>
                        ))}
                    </div>
                </div>
                <div className="border-t border-white/10 pt-8 mt-12 flex flex-wrap items-center justify-between">
                    <div className="">
                        <h3 className="text-sm leading-6 font-semibold text-white">{formHeadline}</h3>
                        <p className="mt-2 text-sm leading-6 text-gray-300">{formParagraph}</p>
                    </div>
                    <div className="max-w-md">
                        <form className="flex mt-0" onSubmit={handleSubmit}>
                            <div className="flex flex-col space-y-4">
                                <label htmlFor={fieldId('email-address')} className="sr-only">
                                    Email address
                                </label>
                                <div
                                    aria-hidden="true"
                                    style={{
                                        position: 'absolute',
                                        width: '1px',
                                        height: '1px',
                                        overflow: 'hidden',
                                        clip: 'rect(0 0 0 0)',
                                        whiteSpace: 'nowrap',
                                        border: 0,
                                        padding: 0,
                                        margin: '-1px',
                                    }}>
                                    <label htmlFor={fieldId('hp_field')}>Leave this field empty</label>
                                    <input
                                        type="text"
                                        name="hp_field"
                                        id={fieldId('hp_field')}
                                        onChange={handleChange}
                                        value={formData.hp_field}
                                        autoComplete="off"
                                        tabIndex={-1}
                                    />
                                </div>
                                <input
                                  id={fieldId('email-address')}
                                  name="email"
                                  type="email"
                                  autoComplete="email"
                                  required
                                  onChange={handleChange}
                                  className="min-w-0 appearance-none rounded-md border-0 bg-white/5 px-1.5 text-white shadow-sm ring-1 ring-inset ring-white/10 w-56 text-sm leading-6"
                                  placeholder={formMailPlaceholder}
                                />
                                <p className="mt-4 text-gray-300 text-base">{formDataPrivacy}</p>
                                <div className="relative flex items-start">
                                    <div className="flex h-6 items-center">
                                        <input
                                           aria-describedby={fieldId('consent-description')}
                                           id={fieldId('consent')}
                                           name="consent"
                                           type="checkbox"
                                           onChange={handleChange}
                                           className="h-4 w-4 rounded-md border-gray-300 text-gray-600"
                                        />
                                    </div>
                                    <div className="ml-3 text-sm leading-6">
                                        <span id={fieldId('consent-description')} className="text-gray-300">
                                                {formDataPrivacyAccepted}
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-0 flex-shrink-0">
                                    <button
                                      type="submit"
                                      className="flex-none rounded-md bg-leaderfy-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-leaderfy-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaderfy-600">
                                        {formSubmit}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="flex mt-8 border-t border-white/10 pt-8 items-center justify-between">
                    <p className="text-xs leading-5 text-gray-400 justify-center items-center mb-0 order-1">&copy; {new Date().getFullYear()} &middot; Leaderfy GmbH</p>
                    <div className="order-2">
                        <a href="https://de.linkedin.com/company/leaderfygmbh" className="text-gray-300">
                            <span className="sr-only">LinkedIn</span>
                            <svg fill="currentColor" viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
                                <path d="M24,4H6C4.895,4,4,4.895,4,6v18c0,1.105,0.895,2,2,2h18c1.105,0,2-0.895,2-2V6C26,4.895,25.105,4,24,4z M10.954,22h-2.95 v-9.492h2.95V22z M9.449,11.151c-0.951,0-1.72-0.771-1.72-1.72c0-0.949,0.77-1.719,1.72-1.719c0.948,0,1.719,0.771,1.719,1.719 C11.168,10.38,10.397,11.151,9.449,11.151z M22.004,22h-2.948v-4.616c0-1.101-0.02-2.517-1.533-2.517 c-1.535,0-1.771,1.199-1.771,2.437V22h-2.948v-9.492h2.83v1.297h0.04c0.394-0.746,1.356-1.533,2.791-1.533 c2.987,0,3.539,1.966,3.539,4.522V22z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
            {showNotification.show && (
              <div className="relative z-10" aria-labelledby={fieldId('modal-title')} role="dialog" aria-modal="true">
                  <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
                  <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                  <div className="sm:flex sm:items-start">
                                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                          {showNotification.consent && (
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="h-6 w-6 text-opacity-100 text-green-400">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                          </svg>
                                          )}
                                      </div>
                                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                          <h3 className="text-base font-semibold leading-6 text-gray-900" id={fieldId('modal-title')}>{showNotification.consent ? notificationSuccessTitle : notificationErrorTitle}</h3>
                                          <div className="mt-2">
                                              <p className="text-sm text-gray-500">{showNotification.message || (showNotification.consent ? notificationSuccessMessage : notificationErrorMessage)}</p>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                  <button onClick={closeNotificationMessage}  type="button" className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">Schließen</button>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
            )}
        </footer>
    );
}

export default Footer